import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Save, Trash2, Wallet, Copy, BookmarkPlus, LayoutTemplate, Lock, GripVertical } from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";
import { useAccounts } from "../lib/accounts";
import { useAuth } from "../lib/auth";

const CATEGORIES = [
  "Food",
  "Groceries",
  "Transport",
  "Shopping",
  "Entertainment",
  "Bills",
  "Income",
  "Subscriptions",
  "Gifts",
  "Other",
];

const INITIAL_ROWS = 10;

/**
 * Spreadsheet-style bulk manual transaction entry.
 * - 10 blank rows to start, "+ Add row" appends more
 * - Tab moves cell → cell → row → row (native browser behavior since we use real <input>)
 * - Each row: Date · Payee · Amount · Type · Category
 * - Rows default cleared=true (user is entering historical/known txns)
 * - Empty rows (no payee OR amount≤0) silently dropped by the backend
 */
function makeBlankRow(defaultDate) {
  return {
    // Stable per-row id — required so React keys survive drag-to-reorder
    // (framer-motion's Reorder compares Reorder.Item `value` prop by reference)
    _key:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    date: defaultDate,
    payee: "",
    amount: "",
    type: "withdrawal",
    category: "Other",
  };
}

export default function BulkAdd() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { accounts, selectedId } = useAccounts();
  const canTemplates = user?.modules?.bulk_templates === true;
  const [targetAccountId, setTargetAccountId] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState(() =>
    Array.from({ length: INITIAL_ROWS }, () => makeBlankRow(today))
  );
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  // Register balance of the currently-selected target account. Powers the
  // "would go below $0" warning on the running-total pill. Null until fetched.
  const [targetBalance, setTargetBalance] = useState(null);

  useEffect(() => {
    if (!targetAccountId && (selectedId || accounts[0]?.id)) {
      setTargetAccountId(selectedId || accounts[0].id);
    }
  }, [selectedId, accounts, targetAccountId]);

  // Fetch the target account's register balance whenever the picker changes.
  useEffect(() => {
    if (!targetAccountId) {
      setTargetBalance(null);
      return;
    }
    let cancelled = false;
    api
      .get(`/summary?account_id=${targetAccountId}`)
      .then((r) => {
        if (!cancelled) setTargetBalance(r.data?.register_balance ?? null);
      })
      .catch(() => {
        if (!cancelled) setTargetBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [targetAccountId]);

  useEffect(() => {
    api
      .get("/bulk-templates")
      .then((r) => setTemplates(r.data))
      .catch(() => setTemplates([]));
  }, []);

  const loadTemplate = (tpl) => {
    // Reset rows to template's items, each with today's date + a few blanks appended.
    // Every row gets a fresh _key so drag-to-reorder works out of the gate.
    const newRows = tpl.items.map((it) => ({
      ...makeBlankRow(today),
      payee: it.payee || "",
      amount: String(it.amount ?? ""),
      type: it.type || "withdrawal",
      category: it.category || "Other",
    }));
    while (newRows.length < INITIAL_ROWS) newRows.push(makeBlankRow(today));
    setRows(newRows);
    setShowTemplates(false);
    toast.success(`Loaded template "${tpl.name}" — ${tpl.items.length} row${tpl.items.length === 1 ? "" : "s"}`);
  };

  const saveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name) {
      toast.error("Give the template a name first");
      return;
    }
    const items = rows
      .filter((r) => r.payee.trim() && parseFloat(r.amount) > 0)
      .map((r) => ({
        payee: r.payee.trim(),
        amount: parseFloat(r.amount),
        type: r.type,
        category: r.category || "Other",
      }));
    if (items.length === 0) {
      toast.error("Fill at least one row (payee + amount) before saving");
      return;
    }
    try {
      const { data } = await api.post("/bulk-templates", { name, items });
      setTemplates((prev) => [data, ...prev]);
      setSavingTemplate(false);
      setTemplateName("");
      toast.success(`Template "${name}" saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deleteTemplate = async (tid) => {
    try {
      await api.delete(`/bulk-templates/${tid}`);
      setTemplates((prev) => prev.filter((t) => t.id !== tid));
      toast.success("Template deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const targetAccount = accounts.find((a) => a.id === targetAccountId);

  const updateRow = (i, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  };

  const removeRow = (i) => {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  };

  const duplicateRow = (i) => {
    setRows((prev) => {
      const next = [...prev];
      // Clone the row but give the duplicate its OWN _key so React/framer-motion
      // don't collide two Reorder.Items on the same identity.
      next.splice(i + 1, 0, { ...prev[i], _key: makeBlankRow(today)._key });
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, makeBlankRow(today)]);
  };

  const filledRows = rows.filter(
    (r) => r.payee.trim() && parseFloat(r.amount) > 0
  );
  const filledCount = filledRows.length;
  // Net total (deposits +, withdrawals −). Kept in raw dollars for the pill.
  const netTotal = filledRows.reduce((sum, r) => {
    const amt = parseFloat(r.amount) || 0;
    return sum + (r.type === "deposit" ? amt : -amt);
  }, 0);

  const saveAll = async () => {
    if (filledCount === 0) {
      toast.error("Add at least one row (payee + amount) first");
      return;
    }
    setBusy(true);
    try {
      const items = rows
        .filter((r) => r.payee.trim() && parseFloat(r.amount) > 0)
        .map((r) => ({
          date: r.date,
          payee: r.payee.trim(),
          amount: parseFloat(r.amount),
          type: r.type,
          category: r.category || "Other",
        }));
      const { data } = await api.post("/transactions/bulk-add", {
        items,
        account_id: targetAccountId || undefined,
      });
      toast.success(
        `Saved ${data.created} transaction${data.created === 1 ? "" : "s"}${
          targetAccount ? ` to ${targetAccount.name}` : ""
        }`
      );
      nav("/register");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bulk-add-page">
      <div className="flex items-center justify-between">
        <Link
          to="/register"
          className="nb-btn nb-btn-secondary !py-2 !px-3"
          data-testid="bulk-back"
        >
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Bulk Add</h1>
        <div className="w-[70px]" />
      </div>

      {/* Live running total — updates as user types. Projects target account's
          post-save balance and turns red if it would go below $0. */}
      {(() => {
        // Compute projection + color band inline so the JSX below stays tidy.
        const projected =
          targetBalance != null ? targetBalance + netTotal : null;
        const wouldOverdraw =
          projected != null && filledCount > 0 && projected < 0;
        const wouldRunLow =
          !wouldOverdraw &&
          projected != null &&
          filledCount > 0 &&
          projected < 25;
        const pillBg =
          filledCount === 0
            ? "#f4f0eb"
            : wouldOverdraw
            ? "#ffd6d6"
            : wouldRunLow
            ? "#fff3c4"
            : netTotal >= 0
            ? "var(--positive)"
            : "#ffd6d6";
        return (
          <div
            data-testid="bulk-running-total"
            data-warning={wouldOverdraw ? "overdraw" : wouldRunLow ? "low" : "ok"}
            className={`nb-card p-3 sticky top-0 z-30 space-y-1 ${filledCount === 0 ? "opacity-70" : ""}`}
            style={{ background: pillBg }}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                {filledCount === 0
                  ? "No rows filled yet"
                  : `${filledCount} row${filledCount === 1 ? "" : "s"} · net`}
              </div>
              <div
                data-testid="bulk-running-total-value"
                className="font-display font-black text-lg tabular-nums"
              >
                {filledCount === 0
                  ? "—"
                  : `${netTotal >= 0 ? "+" : "−"}$${Math.abs(netTotal).toFixed(2)}`}
              </div>
            </div>
            {targetBalance != null && filledCount > 0 && (
              <div
                data-testid="bulk-projected-balance"
                className="flex items-center justify-between gap-2 text-[11px] font-medium text-[#1a1a1a]/80"
              >
                <span>
                  After save: <b>${targetBalance.toFixed(2)}</b>{" "}
                  {netTotal !== 0 && (
                    <>
                      {netTotal >= 0 ? "+" : "−"}${Math.abs(netTotal).toFixed(2)}{" "}
                      = <b>${projected.toFixed(2)}</b>
                    </>
                  )}
                </span>
                {wouldOverdraw && (
                  <span className="text-[10px] font-black uppercase tracking-wider text-[#a00]">
                    ⚠ Would overdraw
                  </span>
                )}
                {wouldRunLow && (
                  <span className="text-[10px] font-black uppercase tracking-wider text-[#a06a00]">
                    ⚠ Running low
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {accounts.length > 0 && (
        <div
          className="nb-card p-3 space-y-2"
          data-testid="bulk-account-picker"
          style={{ background: targetAccount?.color || "#fff" }}
        >
          <div className="flex items-center gap-1.5">
            <Wallet size={14} strokeWidth={3} />
            <div className="label !mb-0">Add to which account?</div>
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {accounts.map((a) => (
              <button
                key={a.id}
                data-testid={`bulk-target-${a.name}`}
                onClick={() => setTargetAccountId(a.id)}
                className={`chip whitespace-nowrap flex items-center gap-1.5 ${targetAccountId === a.id ? "active" : ""}`}
                style={
                  targetAccountId === a.id
                    ? { background: a.color, borderColor: "#1a1a1a" }
                    : {}
                }
              >
                <Wallet size={12} strokeWidth={3} />
                {a.name}
              </button>
            ))}
          </div>
          <div className="text-[11px] font-medium text-[#4a4a4a]">
            All rows below land in{" "}
            <span className="font-black">{targetAccount?.name || "(pick one)"}</span>
            . They&apos;re saved <span className="font-black">cleared</span> —
            edit any row later from the Register.
          </div>
        </div>
      )}

      {/* Templates bar — Pro feature */}
      {canTemplates ? (
      <div className="nb-card p-3 space-y-2" data-testid="templates-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="templates-toggle"
            onClick={() => setShowTemplates((s) => !s)}
            className="nb-btn nb-btn-secondary !py-1.5 !px-3 text-xs"
          >
            <LayoutTemplate size={14} strokeWidth={3} />
            Templates {templates.length > 0 && `(${templates.length})`}
          </button>
          <button
            data-testid="save-template-toggle"
            onClick={() => setSavingTemplate((s) => !s)}
            className="nb-btn nb-btn-secondary !py-1.5 !px-3 text-xs ml-auto"
          >
            <BookmarkPlus size={14} strokeWidth={3} />
            Save current as template
          </button>
        </div>

        {savingTemplate && (
          <div className="flex gap-2" data-testid="save-template-form">
            <input
              type="text"
              placeholder="Template name (e.g. Weekly bills)"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="nb-input !py-1.5 text-sm flex-1"
              data-testid="template-name-input"
              maxLength={60}
              autoFocus
            />
            <button
              data-testid="save-template-confirm"
              onClick={saveAsTemplate}
              className="nb-btn nb-btn-dark !py-1.5 !px-3 text-xs"
            >
              Save
            </button>
          </div>
        )}

        {showTemplates && (
          <div className="space-y-1.5" data-testid="templates-list">
            {templates.length === 0 ? (
              <div className="text-xs font-medium text-[#4a4a4a] italic py-1">
                No templates yet. Fill some rows, then tap &quot;Save current as
                template&quot; above.
              </div>
            ) : (
              templates.map((t) => (
                <div
                  key={t.id}
                  data-testid={`template-${t.id}`}
                  className="flex items-center gap-2 py-1.5 px-2 border-2 border-[#1a1a1a]/20 rounded-lg"
                >
                  <button
                    onClick={() => loadTemplate(t)}
                    data-testid={`load-template-${t.id}`}
                    className="text-left flex-1 min-w-0"
                  >
                    <div className="font-bold text-sm truncate">{t.name}</div>
                    <div className="text-[11px] font-medium text-[#4a4a4a]">
                      {t.items.length} row{t.items.length === 1 ? "" : "s"}
                    </div>
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.id)}
                    data-testid={`delete-template-${t.id}`}
                    className="nb-btn nb-btn-secondary !py-1 !px-2"
                    title="Delete template"
                  >
                    <Trash2 size={12} strokeWidth={3} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      ) : (
        <div
          className="nb-card p-3 flex items-center gap-3"
          data-testid="templates-locked"
        >
          <div className="w-9 h-9 rounded-lg bg-[#fff3c4] border-2 border-[#1a1a1a] flex items-center justify-center shrink-0">
            <Lock size={16} strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black">
              Bulk-add Templates <span className="text-[10px] font-bold ml-1 px-1.5 py-0.5 rounded bg-[#1a1a1a] text-white align-middle">PRO</span>
            </div>
            <div className="text-[11px] font-medium text-[#4a4a4a]">
              Save common lineups (rent, weekly bills, allowance) and reload with one tap.
            </div>
          </div>
          <Link
            to="/upgrade"
            data-testid="templates-upgrade-cta"
            className="nb-btn nb-btn-dark !py-1.5 !px-3 text-xs shrink-0"
          >
            Upgrade
          </Link>
        </div>
      )}

      {/* Header row (desktop only) — deprecated in favor of stacked layout */}

      {/* Rows — drag-to-reorder via the grip handle on the top-left of each card */}
      <Reorder.Group
        axis="y"
        values={rows}
        onReorder={setRows}
        className="space-y-2 list-none pl-0"
        data-testid="bulk-rows-list"
      >
        {rows.map((r, i) => (
          <BulkRow
            key={r._key}
            row={r}
            i={i}
            canRemove={rows.length > 1}
            onUpdate={(patch) => updateRow(i, patch)}
            onDuplicate={() => duplicateRow(i)}
            onRemove={() => removeRow(i)}
          />
        ))}
      </Reorder.Group>

      <button
        onClick={addRow}
        data-testid="bulk-add-row-btn"
        className="nb-btn nb-btn-secondary w-full !py-3"
      >
        <Plus size={16} strokeWidth={3} /> Add another row
      </button>

      <div className="sticky bottom-40 md:bottom-4 z-20">
        <button
          onClick={saveAll}
          disabled={busy || filledCount === 0}
          data-testid="bulk-save-btn"
          className="nb-btn nb-btn-dark w-full !py-4"
        >
          <Save size={18} strokeWidth={3} />
          {busy
            ? "Saving…"
            : `Save all${filledCount ? ` ${filledCount}` : ""}`}
        </button>
      </div>
      {/* Spacer so the last row is never hidden behind the FAB + bottom nav */}
      <div className="h-24 md:h-4" aria-hidden="true" />
    </div>
  );
}

/**
 * Single bulk-add row. Broken out so each instance gets its own
 * `useDragControls()` — required by framer-motion's `Reorder.Item` when we want
 * only the grip handle (not the whole card body) to initiate drag.
 * dragListener=false + dragControls means inputs remain fully editable and
 * drag is triggered exclusively by the GripVertical handle.
 */
function BulkRow({ row, i, canRemove, onUpdate, onDuplicate, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={row}
      dragListener={false}
      dragControls={controls}
      data-testid={`bulk-row-${i}`}
      className="nb-card p-3 space-y-2"
    >
      {/* Line 0: Drag handle + row number label */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`bulk-drag-${i}`}
          onPointerDown={(e) => {
            e.preventDefault();
            controls.start(e);
          }}
          className="touch-none cursor-grab active:cursor-grabbing p-1 -ml-1 text-[#4a4a4a] hover:text-[#1a1a1a] transition-colors"
          aria-label={`Drag row ${i + 1} to reorder`}
          title="Drag to reorder"
        >
          <GripVertical size={16} strokeWidth={3} />
        </button>
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#4a4a4a] select-none">
          Row {i + 1}
        </div>
      </div>
      {/* Line 1: Payee (dominant) + Amount */}
      <div className="grid grid-cols-[1fr_7rem] gap-2 items-center">
        <input
          data-testid={`bulk-payee-${i}`}
          type="text"
          placeholder={`Row ${i + 1} — payee`}
          value={row.payee}
          onChange={(e) => onUpdate({ payee: e.target.value })}
          className="nb-input !py-2 text-sm font-bold"
        />
        <input
          data-testid={`bulk-amount-${i}`}
          type="number"
          step="0.01"
          inputMode="decimal"
          placeholder="0.00"
          value={row.amount}
          onChange={(e) => onUpdate({ amount: e.target.value })}
          className="nb-input !py-2 text-sm text-right font-display font-black"
        />
      </div>
      {/* Line 2: Date, Type, Category */}
      <div className="grid grid-cols-[5.5rem_1fr_1fr] sm:grid-cols-[8.5rem_1fr_1fr] gap-2 items-center">
        <input
          data-testid={`bulk-date-${i}`}
          type="date"
          value={row.date}
          onChange={(e) => onUpdate({ date: e.target.value })}
          className="nb-input !py-1.5 !text-xs min-w-0"
        />
        <select
          data-testid={`bulk-type-${i}`}
          value={row.type}
          onChange={(e) => onUpdate({ type: e.target.value })}
          className="nb-input !py-1.5 !px-2 !text-xs min-w-0"
        >
          <option value="withdrawal">Withdrawal</option>
          <option value="deposit">Deposit</option>
        </select>
        <select
          data-testid={`bulk-category-${i}`}
          value={row.category}
          onChange={(e) => onUpdate({ category: e.target.value })}
          className="nb-input !py-1.5 !px-2 !text-xs min-w-0"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      {/* Line 3: row actions */}
      <div className="flex items-center justify-end gap-2">
        <button
          data-testid={`bulk-duplicate-${i}`}
          onClick={onDuplicate}
          disabled={!row.payee.trim() && !row.amount}
          className="nb-btn nb-btn-secondary !py-1.5 !px-3 !text-xs"
          title="Duplicate this row (below)"
        >
          <Copy size={13} strokeWidth={3} />
          <span>Duplicate</span>
        </button>
        <button
          data-testid={`bulk-remove-${i}`}
          onClick={onRemove}
          disabled={!canRemove}
          className="nb-btn nb-btn-secondary !py-1.5 !px-3 !text-xs"
          title="Remove row"
        >
          <Trash2 size={13} strokeWidth={3} />
          <span>Delete</span>
        </button>
      </div>
    </Reorder.Item>
  );
}
